import { useEffect, useState } from 'react';
import { BillingCtaButton } from '~/design-system/components/v1_5/Settings';
import { cn } from '~/lib/utils';
import styles from './CookieBanner.module.css';

const STORAGE_KEY = 'deck-cookie-consent';

type ConsentStatus = 'accepted' | 'declined';

export interface CookieBannerProps {
  className?: string;
  message?: string;
  linkLabel?: string;
  linkHref?: string;
  acceptLabel?: string;
  declineLabel?: string;
  /**
   * Which skin the two buttons wear, and it is the PAGE's tone rather than the reader's appearance
   * preference — the v2 landing is dark for everyone. Same call the pricing section makes.
   */
  theme?: 'light' | 'dark';
}

export function CookieBanner({
  className,
  message = 'Deck uses cookies to provide a better experience. See our',
  linkLabel = 'Cookie Policy',
  linkHref = '/privacy',
  acceptLabel = 'Accept',
  declineLabel = 'Decline',
  theme = 'light',
}: CookieBannerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored !== 'accepted' && stored !== 'declined') {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const recordConsent = (status: ConsentStatus) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, status);
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className={cn(styles.root, className)}
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-name="CookieBanner"
    >
      <p className={styles.message}>
        {message}{' '}
        <a className={styles.link} href={linkHref}>
          {linkLabel}
        </a>{' '}
        for more information.
      </p>
      {/* The same pair the pricing cards draw: `soft` for the secondary answer, `key` for the one the
          banner is asking for. Consistency with the page's other buttons is the whole point — this
          used to be two legacy `Button`s at a third size with their own hover. */}
      <div className={styles.actions}>
        <BillingCtaButton skin="soft" theme={theme} onClick={() => recordConsent('declined')}>
          {declineLabel}
        </BillingCtaButton>
        <BillingCtaButton skin="key" theme={theme} onClick={() => recordConsent('accepted')}>
          {acceptLabel}
        </BillingCtaButton>
      </div>
    </div>
  );
}

CookieBanner.displayName = 'CookieBanner';
